import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { MapConceptView, MapStepView, ModelMapView } from '../../types/agent';

/** The map of a model by concept (sculpt-api PLAN-pf-skills.md §7b, `model-map`), folded behind one button under
 *  the model's account of it until the user opens it. Opened: the chains to the key outputs — per concept holding
 *  outputs, the tree of the concepts feeding it, the heaviest first, with the lines read — then where each family of
 *  assumptions sits. Every concept is a link selecting its Zone. Drawn from the `model_map` tool's card, so the map is
 *  complete and costs no tokens. */
export function MapCard({ view, onShowCell }: {
  view: ModelMapView;
  onShowCell?: (sheet: string, cell: string) => void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  if (!open) {
    return (
      <div className="animate-pc-in-enter overflow-hidden rounded-lg border border-line-card bg-white" data-testid="map-card">
        <button type="button" data-testid="map-open" onClick={() => setOpen(true)}
          className="flex w-full items-center gap-1.5 px-3 py-2 text-left text-[12px] text-ink hover:bg-hover">
          <span className="text-muted">▸</span>
          <span>{t('modelMap.show', { chains: view.chains.length, families: view.inventory.length })}</span>
        </button>
      </div>
    );
  }
  return (
    <div className="animate-pc-in-enter overflow-hidden rounded-lg border border-line-card bg-white" data-testid="map-card">
      <div className="border-b border-line-row px-3 py-2">
        <button type="button" data-testid="map-close" onClick={() => setOpen(false)}
          className="flex w-full items-center gap-1.5 text-left text-[12.5px] font-medium text-ink">
          <span className="text-muted">▾</span>
          <span>{t('modelMap.title')}</span>
        </button>
        <div className="mt-0.5 text-[11px] text-muted">{t('modelMap.counts', { concepts: view.counts.concepts, links: view.counts.links })}</div>
      </div>

      {view.chains.length > 0 && (
        <Section title={t('modelMap.chains')} note={t('modelMap.chainsNote')} testId="map-chains">
          {view.chains.map((c) => (
            <div key={c.concept.id} className="pb-1" data-testid="map-chain">
              <div className="flex min-w-0 items-baseline gap-1.5 px-3 pt-1 text-[11.5px]">
                <span className="shrink-0 font-medium text-ink">{c.outputs.join(', ')}</span>
                <Place concept={c.concept} onSelect={onShowCell} />
              </div>
              {c.contributors.map((s) => <StepRow key={s.id} step={s} depth={1} onShowCell={onShowCell} />)}
              {c.inputs.length > 0 && (
                <div className="pl-5 pr-3 text-[10.5px] text-muted" data-testid="map-inputs">
                  {t('modelMap.inputs', { families: families(c.inputs) })}
                </div>
              )}
            </div>
          ))}
        </Section>
      )}

      {view.inventory.length > 0 && (
        <Section title={t('modelMap.inventory')} testId="map-inventory">
          {view.inventory.map((f) => (
            <div key={f.family} className="flex min-w-0 flex-wrap items-baseline gap-x-1.5 px-3 py-0.5 text-[11.5px]" data-testid="map-family">
              <span className="shrink-0 font-medium text-ink">{t(`modelMap.family.${f.family}`, { defaultValue: f.family })}</span>
              {f.concepts.slice(0, 4).map((c) => <Place key={c.id} concept={c} onSelect={onShowCell} withName />)}
              {f.concepts.length > 4 && <span className="text-[10.5px] text-muted">{t('modelMap.more', { count: f.concepts.length - 4 })}</span>}
            </div>
          ))}
        </Section>
      )}
    </div>
  );
}

function families(concepts: MapConceptView[]): string {
  return [...new Set(concepts.map((c) => c.family ?? 'other'))].join(', ');
}

function Section({ title, note, testId, children }: { title: string; note?: string; testId?: string; children: React.ReactNode }) {
  return (
    <div className="border-t border-line-row py-1 first:border-t-0" data-testid={testId}>
      <div className="px-3 pt-1 text-[11.5px] font-medium text-ink">{title}</div>
      {note && <div className="px-3 text-[11px] text-muted">{note}</div>}
      {children}
    </div>
  );
}

/** A contributor and, under it, what feeds it: the concept, the lines read, a link selecting its Zone. */
function StepRow({ step, depth, onShowCell }: { step: MapStepView; depth: number; onShowCell?: (sheet: string, cell: string) => void }) {
  const { t } = useTranslation();
  return (
    <>
      <div className="flex min-w-0 items-baseline gap-1.5 py-0.5 pr-3 text-[11.5px]" style={{ paddingLeft: 12 + depth * 10 }} data-testid="map-step">
        <span className="shrink-0 text-muted">←</span>
        <span className="min-w-0 flex-1 truncate text-ink" title={step.name}>{step.name}</span>
        <span className="shrink-0 font-mono text-[10.5px] text-muted">{t('modelMap.lines', { count: step.weight })}</span>
        <Place concept={step} onSelect={onShowCell} />
      </div>
      {step.feeds.map((k) => <StepRow key={k.id} step={k} depth={depth + 1} onShowCell={onShowCell} />)}
    </>
  );
}

function Place({ concept, onSelect, withName }: { concept: MapConceptView; onSelect?: (sheet: string, address: string) => void; withName?: boolean }) {
  if (!concept.address) return withName ? <span className="text-[11px] text-ink">{concept.name}</span> : null;
  const label = withName ? concept.name : `${concept.sheet}!${concept.address}`;
  const title = `${concept.sheet}!${concept.address}`;
  if (!onSelect) return <span className="max-w-[45%] shrink-0 truncate font-mono text-[10.5px] text-muted" title={title}>{label}</span>;
  return (
    <button type="button" data-testid="map-place" onClick={() => onSelect(concept.sheet, concept.address as string)} title={title}
      className={`max-w-[45%] shrink-0 truncate text-accent underline-offset-2 hover:underline ${withName ? 'text-[11px]' : 'font-mono text-[10.5px]'}`}>
      {label}
    </button>
  );
}
